import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { SITE_BASE_URL } from "../../data/agenticSeoUtils";
import {
  FaBuildingColumns,
  FaCheck,
  FaHeartPulse,
  FaIndustry,
  FaLandmark,
  FaStore,
  FaTruck,
  FaUsers,
  FaShieldHalved,
  FaTowerCell,
  FaBolt,
  FaLandmarkFlag,
  FaBuilding,
  FaGraduationCap,
  FaHotel,
  FaFilm,
  FaWheatAwn,
  FaLaptopCode,
  FaPlane,
  FaCar,
  FaPills,
} from "react-icons/fa6";
import {
  AGENTIC_AI_DOMAINS,
  AGENTIC_AI_ENTERPRISE_USE_CASES,
  domainPathFromLabel,
} from "../../data/agenticDomains";
import { AiOutlineRight } from "react-icons/ai";
import { FINANCIAL_DOMAIN_INTRO } from "../../data/financialUseCases";
import { MANUFACTURING_DOMAIN_INTRO } from "../../data/manufacturingUseCases";
import { HR_DOMAIN_INTRO } from "../../data/hrUseCases";
import { BFSI_DOMAIN_INTRO } from "../../data/bfsiUseCases";
import { HEALTHCARE_DOMAIN_INTRO } from "../../data/healthcareUseCases";
import { LOGISTICS_DOMAIN_INTRO } from "../../data/logisticsUseCases";
import { RETAIL_DOMAIN_INTRO } from "../../data/retailUseCases";
import { INSURANCE_DOMAIN_INTRO } from "../../data/insuranceUseCases";
import { TELECOM_DOMAIN_INTRO } from "../../data/telecomUseCases";
import { PUBLIC_SECTOR_DOMAIN_INTRO } from "../../data/public-sectorUseCases";
import { REAL_ESTATE_DOMAIN_INTRO } from "../../data/real-estateUseCases";
import { HOSPITALITY_DOMAIN_INTRO } from "../../data/hospitalityUseCases";
import { AGRICULTURE_DOMAIN_INTRO } from "../../data/agricultureUseCases";
import { IT_SERVICES_DOMAIN_INTRO } from "../../data/it-servicesUseCases";
import { AVIATION_DOMAIN_INTRO } from "../../data/aviationUseCases";
import { AUTOMOTIVE_DOMAIN_INTRO } from "../../data/automotiveUseCases";
import { PHARMA_DOMAIN_INTRO } from "../../data/pharmaUseCases";
import { MEDIA_DOMAIN_INTRO } from "../../data/mediaUseCases";
import { ENERGY_DOMAIN_INTRO } from "../../data/energyUseCases";
import { EDUCATION_DOMAIN_INTRO } from "../../data/educationUseCases";

const DOMAIN_META = {
  financial: { intro: FINANCIAL_DOMAIN_INTRO, icon: FaLandmark },
  manufacturing: { intro: MANUFACTURING_DOMAIN_INTRO, icon: FaIndustry },
  hr: { intro: HR_DOMAIN_INTRO, icon: FaUsers },
  bfsi: { intro: BFSI_DOMAIN_INTRO, icon: FaBuildingColumns },
  healthcare: { intro: HEALTHCARE_DOMAIN_INTRO, icon: FaHeartPulse },
  logistics: { intro: LOGISTICS_DOMAIN_INTRO, icon: FaTruck },
  retail: { intro: RETAIL_DOMAIN_INTRO, icon: FaStore },
  insurance: { intro: INSURANCE_DOMAIN_INTRO, icon: FaShieldHalved },
  telecom: { intro: TELECOM_DOMAIN_INTRO, icon: FaTowerCell },
  "public-sector": { intro: PUBLIC_SECTOR_DOMAIN_INTRO, icon: FaLandmarkFlag },
  "real-estate": { intro: REAL_ESTATE_DOMAIN_INTRO, icon: FaBuilding },
  hospitality: { intro: HOSPITALITY_DOMAIN_INTRO, icon: FaHotel },
  agriculture: { intro: AGRICULTURE_DOMAIN_INTRO, icon: FaWheatAwn },
  "it-services": { intro: IT_SERVICES_DOMAIN_INTRO, icon: FaLaptopCode },
  aviation: { intro: AVIATION_DOMAIN_INTRO, icon: FaPlane },
  automotive: { intro: AUTOMOTIVE_DOMAIN_INTRO, icon: FaCar },
  pharma: { intro: PHARMA_DOMAIN_INTRO, icon: FaPills },
  media: { intro: MEDIA_DOMAIN_INTRO, icon: FaFilm },
  energy: { intro: ENERGY_DOMAIN_INTRO, icon: FaBolt },
  education: { intro: EDUCATION_DOMAIN_INTRO, icon: FaGraduationCap },
};

function useWindowWidth() {
  const [width, setWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1200
  );
  useEffect(() => {
    const handler = () => setWidth(window.innerWidth);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return width;
}

function AgenticAICapabilities() {
  const location = useLocation();
  const navigate = useNavigate();
  const width = useWindowWidth();
  const isMobile = width < 640;
  const isTablet = width >= 640 && width < 1024;

  useEffect(() => {
    const hash = location.hash?.replace(/^#/, "");
    const target = hash || location.state?.scrollTo;
    if (!target) return;

    const scrollToTarget = () => {
      const el = document.getElementById(target);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    };

    const timer = window.setTimeout(scrollToTarget, 120);
    return () => window.clearTimeout(timer);
  }, [location.hash, location.state, location.pathname]);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const capabilityCards = [
    { title: "AI Agents", text: "Autonomous and semi-autonomous workflows" },
    { title: "Copilots", text: "Human-guided task execution and decision support" },
    { title: "RAG Systems", text: "Grounded answers from enterprise data" },
    { title: "Enterprise Automation", text: "ERP, CRM, APIs, tickets, reports" },
    { title: "Governance", text: "Guardrails, security, and observability" },
    { title: "Scalable Delivery", text: "Cloud-ready architecture and deployment" },
  ];

  const topBottomCards = [
    { title: "LLM & Agent Engineering", text: "Single-agent and multi-agent systems" },
    { title: "RAG & Enterprise Integration", text: "Knowledge-grounded AI with secure connectivity" },
    { title: "Automation to Production", text: "From architecture to deployment and support" },
  ];

  const topMiniCards = [
    { title: "Agentic AI", text: "Planning, reasoning, memory, and execution across multi-step workflows" },
    { title: "Enterprise AI", text: "Secure integration with business systems, data, and knowledge repositories" },
    { title: "Automation", text: "AI combined with workflow orchestration and process automation" },
    { title: "Engineering", text: "Production-grade software delivery with scale, reliability, and governance" },
  ];

  const capabilitySections = [
    {
      title: "Agent Strategy & Solution Architecture",
      items: [
        "AI roadmap aligned to business workflows and measurable outcomes",
        "Use-case discovery workshops for operations, finance, procurement, HR, IT, and customer service",
        "Target operating model for copilots, AI agents, and human-in-the-loop workflows",
        "Reference architectures for secure enterprise deployment",
      ],
    },
    {
      title: "LLM & Agent Engineering",
      items: [
        "Production-grade agents using GPT, Claude, Llama, Gemini, and open-source models",
        "Tool-calling, planning, memory, reflection, and multi-step task execution",
        "Single-agent and multi-agent systems for research, workflow coordination, and decision support",
        "Prompt engineering, structured outputs, retry logic, and failure-safe task design",
      ],
    },
    {
      title: "RAG & Enterprise Knowledge Systems",
      items: [
        "Retrieval pipelines for policies, SOPs, manuals, contracts, tickets, and technical documentation",
        "Embeddings, chunking strategies, metadata enrichment, re-ranking, and query optimization",
        "Vector database integration with Pinecone, Weaviate, Chroma, Milvus, and Azure AI Search",
        "Grounded answers with access control and source-aware responses",
      ],
    },
    {
      title: "Enterprise Integration & Automation",
      items: [
        "Integration with ERP, CRM, ServiceNow, ticketing, DMS, internal portals, and business APIs",
        "Workflow automation for email drafting, document processing, reporting, approvals, and case handling",
        "Event-driven automation and orchestration across legacy and modern systems",
        "Strong fit for hybrid environments combining AI, RPA, and rule-based automation",
      ],
    },
    {
      title: "Data, Evaluation & Model Optimization",
      items: [
        "Accuracy, latency, hallucination, and cost evaluation frameworks",
        "Domain adaptation, tuning strategies, test sets, and benchmark-driven iteration",
        "Observability for prompts, traces, tool calls, failures, and user feedback",
        "Ongoing optimization for business impact, usage quality, and operating cost",
      ],
    },
    {
      title: "Security, Governance & Scale",
      items: [
        "Role-based access, auditability, secure API handling, and data governance",
        "Guardrails for content safety, policy compliance, and sensitive data handling",
        "Cloud-ready deployment on Azure, AWS, and GCP with scalable microservices",
        "Delivery practices for enterprise reliability, maintainability, and controlled rollout",
      ],
    },
  ];

  const useCases = AGENTIC_AI_ENTERPRISE_USE_CASES.map((item) => ({
    title: item.label,
    text: item.text,
    scrollTo: item.scrollTo,
  }));

  const domainUseCases = AGENTIC_AI_DOMAINS.map((domain) => {
    const meta = DOMAIN_META[domain.id] || {};
    return {
      ...domain,
      icon: meta.icon || FaLandmark,
      text: meta.intro?.seoDescription || meta.intro?.content || meta.text || domain.label,
    };
  });

  const openDomain = (label) => {
    navigate(domainPathFromLabel(label));
  };

  const px = (d, t, m) => (isMobile ? m : isTablet ? t : d);
  const metaTitle = "Agentic AI | Surens Inffotek";
  const metaDescription =
    "Enterprise agentic AI capabilities — AI copilots, document intelligence agents, process automation, multi-agent workflows, RAG, and governed LLM engineering for finance, manufacturing, healthcare, and more.";
  const canonicalUrl = `${SITE_BASE_URL}/agentic-ai`;
  const capabilitiesJsonLd = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: metaTitle,
    description: metaDescription,
    url: canonicalUrl,
    isPartOf: {
      "@type": "WebSite",
      name: "Surens Inffotek",
      url: SITE_BASE_URL,
    },
    about: {
      "@type": "Thing",
      name: "Agentic AI Capabilities",
      description: metaDescription,
    },
  };

  return (
    <>
      <Helmet>
        <html lang="en" />
        <title>{metaTitle}</title>
        <meta name="description" content={metaDescription} />
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1" />
        <meta name="author" content="Surens Inffotek" />
        <link rel="canonical" href={canonicalUrl} />
        <link rel="alternate" type="text/plain" title="LLM summary" href={`${SITE_BASE_URL}/llms.txt`} />
        <link rel="alternate" type="text/plain" title="LLM agentic catalog" href={`${SITE_BASE_URL}/llms-full.txt`} />
        <link rel="alternate" type="text/plain" title="LLM RPA catalog" href={`${SITE_BASE_URL}/llms-rpa.txt`} />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="en_US" />
        <meta property="og:site_name" content="Surens Inffotek" />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content={`${SITE_BASE_URL}/logo.png`} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={metaTitle} />
        <meta name="twitter:description" content={metaDescription} />
        <meta name="twitter:image" content={`${SITE_BASE_URL}/logo.png`} />
        <script type="application/ld+json">{JSON.stringify(capabilitiesJsonLd)}</script>
      </Helmet>
      <div
        style={{
          fontFamily: "Arial, sans-serif",
          background: "#eef1f4",
          overflowX: "hidden",
          borderLeft: isMobile ? "4px solid #1cc7b7" : "10px solid #1cc7b7",
          borderRight: isMobile ? "4px solid #1cc7b7" : "10px solid #1cc7b7",
        }}
      >

        <div
          style={{
            background: "#0d3778",
            padding: px("42px 52px 55px", "32px 32px 45px", "24px 20px 36px"),
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: isMobile || isTablet ? "column" : "row",
              justifyContent: "space-between",
              gap: px("45px", "36px", "28px"),
            }}
          >
            <div style={{ width: isMobile || isTablet ? "100%" : "60%" }}>
              <h1
                style={{
                  color: "#edf3ff",
                  fontSize: px("50px", "38px", "28px"),
                  lineHeight: "1.2",
                  fontWeight: "800",
                  fontFamily: "Exo, sans-serif",
                  marginBottom: px("30px", "22px", "18px"),
                  letterSpacing: "1px",
                }}
              >
                Build <span style={{ color: "#ff7d1a" }}>Agentic AI</span> Solutions for Real
                Enterprise Workflows
              </h1>

              <p
                style={{
                  color: "#fff",
                  fontSize: px("18px", "16px", "15px"),
                  lineHeight: "1.5",
                  opacity: 0.96,
                  maxWidth: "650px",
                }}
              >
                We help organizations move beyond AI pilots and build production-ready AI agents,
                copilots, and intelligent workflow systems that can plan, reason, retrieve
                knowledge, connect to enterprise systems, and execute work securely at scale.
              </p>

              <div
                style={{
                  display: "flex",
                  flexDirection: isMobile ? "column" : "row",
                  gap: "16px",
                  marginTop: px("38px", "28px", "22px"),
                }}
              >
                <button
                  type="button"
                  onClick={() => scrollTo("contact")}
                  style={{
                    background: "#ff7d1a",
                    border: "none",
                    color: "#fff",
                    padding: px("15px 20px", "13px 18px", "12px 16px"),
                    borderRadius: "18px",
                    fontSize: px("16px", "15px", "14px"),
                    fontWeight: "700",
                    cursor: "pointer",
                    width: isMobile ? "100%" : "auto",
                  }}
                >
                  Discuss Your AI Initiative
                </button>

                <button
                  type="button"
                  onClick={() => scrollTo("capabilities")}
                  style={{
                    background: "rgba(255,255,255,0.10)",
                    border: "1px solid rgba(255,255,255,0.15)",
                    color: "#fff",
                    padding: px("15px 20px", "13px 18px", "12px 16px"),
                    borderRadius: "18px",
                    fontSize: px("16px", "15px", "14px"),
                    fontWeight: "700",
                    cursor: "pointer",
                    width: isMobile ? "100%" : "auto",
                  }}
                >
                  Explore Capabilities
                </button>
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: isMobile
                    ? "1fr"
                    : isTablet
                    ? "repeat(3, 1fr)"
                    : "repeat(3, auto)",
                  gap: px("18px", "14px", "12px"),
                  marginTop: px("45px", "32px", "24px"),
                }}
              >
                {topBottomCards.map((item, index) => (
                  <div
                    key={index}
                    style={{
                      background: "#315894",
                      borderRadius: "24px",
                      padding: "12px 16px",
                      minHeight: "60px",
                    }}
                  >
                    <h3
                      style={{
                        color: "#ffb26f",
                        fontSize: "14px",
                        lineHeight: "1.5",
                        marginBottom: "6px",
                        fontWeight: "750",
                      }}
                    >
                      {item.title}
                    </h3>
                    <p
                      style={{
                        color: "#fff",
                        fontSize: "13px",
                        lineHeight: "1.55",
                        fontWeight: "500",
                        margin: 0,
                      }}
                    >
                      {item.text}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            <div
              style={{
                width: isMobile || isTablet ? "100%" : "50%",
                background: "#2f4f86",
                borderRadius: "28px",
                padding: "18px",
                display: "grid",
                gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                gap: "16px",
                alignContent: "start",
                height: "fit-content",
                marginTop: isMobile || isTablet ? "0" : "10px",
              }}
            >
              {capabilityCards.map((card, index) => (
                <div
                  key={index}
                  style={{
                    background: "#173f7a",
                    borderRadius: "22px",
                    padding: "10px 14px",
                    minHeight: "50px",
                  }}
                >
                  <h3
                    style={{
                      color: "#fff",
                      fontSize: px("20px", "18px", "16px"),
                      lineHeight: "1.45",
                      marginBottom: "8px",
                      fontWeight: "700",
                    }}
                  >
                    {card.title}
                  </h3>
                  <p
                    style={{
                      color: "#fff",
                      fontSize: "14px",
                      lineHeight: "1.7",
                      opacity: 0.92,
                      fontWeight: "500",
                      margin: 0,
                    }}
                  >
                    {card.text}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div
          style={{
            padding: px("70px 70px 60px", "50px 32px 40px", "32px 20px 28px"),
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : isTablet ? "repeat(2, 1fr)" : "repeat(4, 1fr)",
            gap: px("30px", "20px", "16px"),
          }}
        >
          {topMiniCards.map((card, index) => (
            <div
              key={index}
              style={{
                background: "#fff",
                border: "1px solid #dde4ea",
                borderRadius: "30px",
                padding: "14px 16px",
              }}
            >
              <h3
                style={{
                  color: "#0b3475",
                  fontSize: px("20px", "18px", "17px"),
                  lineHeight: "1.4",
                  marginBottom: "4px",
                  fontWeight: "700",
                  paddingLeft: "10px",
                }}
              >
                {card.title}
              </h3>
              <p
                style={{
                  color: "#587090",
                  fontSize: px("16px", "15px", "14px"),
                  lineHeight: "1.5",
                  paddingLeft: "10px",
                  margin: 0,
                }}
              >
                {card.text}
              </p>
            </div>
          ))}
        </div>

        <div id="capabilities" style={{ padding: px("50px 52px", "40px 32px", "28px 20px") }}>
          <p
            style={{
              color: "#ff7d1a",
              fontWeight: "700",
              letterSpacing: "4px",
              fontSize: "16px",
            }}
          >
            TECHNICAL CAPABILITIES
          </p>

          <h2
            style={{
              color: "#0b3475",
              fontFamily: "Exo, sans-serif",
              fontSize: px("38px", "30px", "24px"),
              lineHeight: "1.08",
              fontWeight: "900",
              marginTop: "20px",
              maxWidth: "800px",
            }}
          >
            End-to-end capability across the Agentic AI stack
          </h2>

          <p
            style={{
              color: "#5b7191",
              fontSize: px("18px", "16px", "15px"),
              lineHeight: "1.5",
              marginTop: "21px",
              maxWidth: "800px",
            }}
          >
            From AI strategy to integration, evaluation, governance, and deployment, we build
            systems that are technically strong and operationally useful.
          </p>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : isTablet ? "1fr" : "1fr 1fr",
              gap: "32px",
              marginTop: "50px",
            }}
          >
            {capabilitySections.map((section, index) => (
              <div
                key={index}
                style={{
                  background: "#fff",
                  borderRadius: "30px",
                  border: "1px solid #dbe2e8",
                  padding: px("28px 32px", "22px 24px", "18px 16px"),
                }}
              >
                <h3
                  style={{
                    color: "#0b3475",
                    fontSize: px("24px", "20px", "18px"),
                    lineHeight: "1.3",
                    marginBottom: "18px",
                    fontWeight: "700",
                    fontFamily: "Exo, sans-serif",
                  }}
                >
                  {section.title}
                </h3>

                {section.items.map((item, i) => (
                  <div
                    key={i}
                    style={{
                      display: "flex",
                      gap: "14px",
                      marginBottom: "14px",
                      alignItems: "flex-start",
                    }}
                  >
                    <div
                      style={{
                        width: "10px",
                        height: "10px",
                        background: "#ff7d1a",
                        borderRadius: "50%",
                        marginTop: "9px",
                        flexShrink: 0,
                      }}
                    />
                    <p
                      style={{
                        color: "#111",
                        fontSize: px("15px", "14px", "14px"),
                        lineHeight: "1.6",
                        margin: 0,
                        fontWeight: "500",
                      }}
                    >
                      {item}
                    </p>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>

        <div
          style={{
            background: "#0b3475",
            padding: px("42px 52px 34px", "36px 32px 28px", "28px 20px 24px"),
            marginTop: "30px",
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: isMobile || isTablet ? "column" : "row",
              justifyContent: "space-between",
              gap: px("55px", "36px", "24px"),
              alignItems: isMobile || isTablet ? "flex-start" : "center",
            }}
          >
            <div style={{ width: isMobile || isTablet ? "100%" : "60%" }}>
              <p
                style={{
                  color: "#ffb06b",
                  letterSpacing: "4px",
                  fontWeight: "800",
                  fontSize: "16px",
                  marginBottom: "18px",
                }}
              >
                TECHNOLOGY STACK
              </p>

              <h2
                style={{
                  color: "#fff",
                  fontSize: px("44px", "32px", "26px"),
                  lineHeight: "1.15",
                  fontWeight: "900",
                  maxWidth: "780px",
                  marginBottom: "24px",
                  fontFamily: "Exo, sans-serif",
                }}
              >
                Tools, frameworks, and engineering foundations
              </h2>

              <p
                style={{
                  color: "#fff",
                  opacity: 0.92,
                  fontSize: px("17px", "15px", "14px"),
                  lineHeight: "1.7",
                  maxWidth: "760px",
                  margin: 0,
                }}
              >
                We work with leading LLM ecosystems, enterprise integration patterns, and
                cloud-native engineering practices to build secure, scalable AI systems.
              </p>
            </div>

            <div
              style={{
                width: isMobile || isTablet ? "100%" : "50%",
                display: "grid",
                gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                columnGap: "12px",
                rowGap: "12px",
                alignContent: "start",
                marginTop: isMobile || isTablet ? "0" : "6px",
              }}
            >
              {[
                "GPT / Claude / Llama / Gemini",
                "LangChain / LlamaIndex / Semantic Kernel / Haystack",
                "Python / FastAPI / Node.js / GraphQL",
                "Azure / AWS / GCP",
                "Vector DBs and embeddings pipelines",
                "REST APIs / Microservices / Event-driven integration",
                "Postgres / Enterprise data platforms",
                "RPA and workflow orchestration",
              ].map((item, index) => (
                <div
                  key={index}
                  style={{
                    background: "#355b97",
                    borderRadius: "16px",
                    padding: px("20px 24px", "16px 20px", "14px 16px"),
                    color: "#fff",
                    fontSize: px("16px", "15px", "14px"),
                    lineHeight: "1.55",
                    fontWeight: "700",
                    minHeight: "40px",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div id="usecases" style={{ padding: px("85px 52px", "60px 32px", "40px 20px") }}>
          <p
            style={{
              color: "#ff7d1a",
              fontWeight: "600",
              letterSpacing: "4px",
              fontSize: "16px",
            }}
          >
            WHERE WE CREATE VALUE
          </p>

          <h2
            style={{
              color: "#0b3475",
              fontSize: px("35px", "28px", "24px"),
              fontFamily: "Exo, sans-serif",
              lineHeight: "1.08",
              fontWeight: "900",
              marginTop: "18px",
              maxWidth: "700px",
            }}
          >
            High-impact use cases for enterprise AI agents
          </h2>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : isTablet ? "repeat(2, 1fr)" : "repeat(3, 1fr)",
              gap: "32px",
              marginTop: "55px",
            }}
          >
            {useCases.map((item, index) => (
              <div
                key={index}
                id={item.scrollTo}
                style={{
                  background: "#fff",
                  borderRadius: "32px",
                  border: "1px solid #dde3ea",
                  padding: px("38px", "28px", "22px"),
                  minHeight: "200px",
                }}
              >
                <h3
                  style={{
                    color: "#0b3475",
                    fontSize: px("24px", "20px", "18px"),
                    fontFamily: "Exo, sans-serif",
                    lineHeight: "1.4",
                    marginBottom: "22px",
                    fontWeight: "700",
                  }}
                >
                  {item.title}
                </h3>
                <p
                  style={{
                    color: "#5d7493",
                    fontSize: px("16px", "15px", "14px"),
                    lineHeight: "1.5",
                    margin: 0,
                  }}
                >
                  {item.text}
                </p>
              </div>
            ))}
          </div>
        </div>

        <section
          id="domain-usecases"
          aria-labelledby="domain-usecases-heading"
          style={{
            background: "#0b3475",
            padding: px("55px 52px 90px", "50px 32px 72px", "40px 20px 60px"),
            scrollMarginTop: "100px",
          }}
        >
          <h3
            id="domain-usecases-heading"
            style={{
              textAlign: "center",
              color: "#fff",
              fontSize: px("36px", "28px", "24px"),
              fontFamily: "Exo, sans-serif",
              lineHeight: "1.1",
              fontWeight: "700",
              marginBottom: px("42px", "32px", "24px"),
            }}
          >
            Domain use cases
          </h3>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile
                ? "1fr"
                : isTablet
                ? "repeat(3, minmax(0, 1fr))"
                : "repeat(5, minmax(0, 1fr))",
              gap: "18px",
              rowGap: "18px",
              maxWidth: "1200px",
              margin: "0 auto",
            }}
          >
         {domainUseCases.map((domain) => {
  const Icon = domain.icon;

  return (
    <Link
      key={domain.id}
      to={domainPathFromLabel(domain.label)}
      style={{
        textDecoration: "none",
        display: "block",
      }}
    >
      <div
        style={{
          background: "#fff",
          borderRadius: "10px",
          minHeight: "76px",
          padding: "12px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "14px",
            minWidth: 0,
          }}
        >
          <div
            style={{
              width: "38px",
              height: "38px",
              borderRadius: "10px",
              background: "#e8eef7",
              color: "#0b3475",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <Icon size={18} />
          </div>

          <span
            style={{
              color: "#0b3475",
              fontSize: "15px",
              fontWeight: "600",
              fontFamily: "Exo, sans-serif",
            }}
          >
            {domain.label}

            {!domain.live && (
              <span
                style={{
                  display: "block",
                  fontSize: "11px",
                  color: "#5d7493",
                  marginTop: "2px",
                }}
              >
                Coming soon
              </span>
            )}
          </span>
        </div>

        <AiOutlineRight
          size={14}
          style={{
            color: "#0b3475",
            flexShrink: 0,
          }}
        />
      </div>
    </Link>
  );
})}                          
          </div>
        </section>

        <div
          id="engage"
          style={{
            padding: px("60px 52px 70px", "48px 32px 50px", "36px 20px 36px"),
            display: "grid",
            gridTemplateColumns: isMobile || isTablet ? "1fr" : "1fr 1fr",
            gap: px("36px", "28px", "24px"),
            alignItems: "start",
          }}
        >
          <div
            style={{
              background: "#082f6a",
              borderRadius: "34px",
              padding: px("34px", "28px", "22px"),
            }}
          >
            <p
              style={{
                color: "#ffb06b",
                fontWeight: "650",
                letterSpacing: "4px",
                fontSize: "15px",
              }}
            >
              WHY SURENSINFOTEK
            </p>

            <h2
              style={{
                color: "#fff",
                fontSize: px("34px", "26px", "22px"),
                lineHeight: "1.18",
                fontWeight: "900",
                marginTop: "18px",
                marginBottom: "24px",
                maxWidth: "580px",
                fontFamily: "Exo, sans-serif",
              }}
            >
              Why companies partner with us for Agentic AI
            </h2>

            {[
              "Engineering-first execution, not just consulting slides",
              "Strong AI + automation background across agentic AI, RPA, QA, and enterprise integration",
              "Offshore delivery model that improves speed, flexibility, and cost efficiency",
              "Capability to build MVPs, pilots, and production-ready systems",
              "Cross-functional delivery covering architecture, development, integration, testing, and support",
              "Focus on measurable business outcomes: productivity, process efficiency, quality, and turnaround time",
            ].map((item, index) => (
              <div
                key={index}
                style={{
                  background: "#284f8b",
                  borderRadius: "20px",
                  padding: "16px 18px",
                  marginBottom: "16px",
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "16px",
                }}
              >
                <div
                  style={{
                    width: "34px",
                    height: "34px",
                    background: "#ff7d1a",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#fff",
                    flexShrink: 0,
                    fontSize: "14px",
                    marginTop: "2px",
                  }}
                >
                  <FaCheck />
                </div>
                <p
                  style={{
                    color: "#fff",
                    fontSize: px("15px", "14px", "14px"),
                    lineHeight: "1.6",
                    margin: 0,
                    fontWeight: "500",
                  }}
                >
                  {item}
                </p>
              </div>
            ))}
          </div>

          <div
            style={{
              background: "#fff",
              borderRadius: "34px",
              padding: px("34px", "28px", "22px"),
              height: "fit-content",
            }}
          >
            <p
              style={{
                color: "#ff7d1a",
                fontWeight: "700",
                letterSpacing: "4px",
                fontSize: "15px",
              }}
            >
              DELIVERY MODEL
            </p>

            <h2
              style={{
                color: "#0b3475",
                fontSize: px("36px", "26px", "22px"),
                lineHeight: "1.12",
                fontWeight: "900",
                marginTop: "18px",
                marginBottom: "28px",
                maxWidth: "520px",
                fontFamily: "Exo, sans-serif",
              }}
            >
              From idea to enterprise-ready rollout
            </h2>

            {[
              { no: "01", title: "Discover", text: "Identify priority workflows, users, data sources, risks, and expected ROI." },
              { no: "02", title: "Design", text: "Define architecture, agent behavior, integrations, governance, and rollout approach." },
              { no: "03", title: "Build", text: "Engineer copilots, agents, RAG pipelines, APIs, orchestration, and monitoring." },
              { no: "04", title: "Validate", text: "Test for accuracy, security, performance, edge cases, and human acceptance." },
              { no: "05", title: "Scale", text: "Deploy, support, optimize, and expand into additional workflows or departments." },
            ].map((step, index) => (
              <div
                key={index}
                style={{
                  background: "#f4f5f7",
                  borderRadius: "22px",
                  padding: "18px",
                  display: "flex",
                  gap: "16px",
                  marginBottom: "16px",
                  alignItems: "flex-start",
                }}
              >
                <div
                  style={{
                    width: "42px",
                    height: "42px",
                    background: "#0b3475",
                    borderRadius: "16px",
                    color: "#fff",
                    fontWeight: "800",
                    fontSize: "16px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  {step.no}
                </div>
                <div>
                  <h3
                    style={{
                      fontSize: px("22px", "18px", "17px"),
                      color: "#0b3475",
                      marginBottom: "6px",
                      fontWeight: "600",
                      marginTop: "2px",
                    }}
                  >
                    {step.title}
                  </h3>
                  <p
                    style={{
                      fontSize: px("15px", "14px", "14px"),
                      lineHeight: "1.6",
                      color: "#627997",
                      margin: 0,
                    }}
                  >
                    {step.text}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div
          id="contact"
          style={{
            background: "#0b3475",
            padding: px("65px 35px", "50px 32px", "36px 20px"),
            borderBottom: "10px solid #1cc7b7",
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: isMobile || isTablet ? "column" : "row",
              justifyContent: "space-between",
              gap: px("55px", "40px", "28px"),
              alignItems: "flex-start",
            }}
          >
            <div style={{ width: isMobile || isTablet ? "100%" : "56%" }}>
              <p
                style={{
                  color: "#ffb06b",
                  fontWeight: "600",
                  letterSpacing: "3px",
                  fontSize: "14px",
                  marginTop: isMobile ? "0" : "40px",
                  marginBottom: "24px",
                }}
              >
                LET&apos;S BUILD TOGETHER
              </p>

              <h2
                style={{
                  color: "#fff",
                  fontSize: px("40px", "30px", "24px"),
                  lineHeight: "1.08",
                  fontWeight: "900",
                  margin: 0,
                  maxWidth: "760px",
                  fontFamily: "Exo, sans-serif",
                  letterSpacing: "-1px",
                }}
              >
                From AI concept to production-ready enterprise agents
              </h2>

              <p
                style={{
                  color: "#fff",
                  opacity: 0.95,
                  fontSize: px("17px", "15px", "14px"),
                  lineHeight: "1.6",
                  marginTop: "32px",
                  maxWidth: "1000px",
                  fontWeight: "400",
                }}
              >
                Whether you need an AI copilot, a secure knowledge assistant, a document
                intelligence workflow, or a multi-agent business process solution, SurensInffotek
                can support your roadmap with strong offshore engineering capability.
              </p>
            </div>

            <div
              style={{
                width: isMobile || isTablet ? "100%" : "37%",
                background: "#35558f",
                borderRadius: "36px",
                padding: px("24px 28px", "22px 24px", "20px 18px"),
                height: "fit-content",
                marginTop: isMobile || isTablet ? "0" : "10px",
                boxSizing: "border-box",
              }}
            >
              <h3
                style={{
                  color: "#fff",
                  fontSize: px("28px", "22px", "20px"),
                  marginBottom: "24px",
                  fontWeight: "800",
                  marginTop: "0",
                }}
              >
                Talk to our team
              </h3>

              {[
                "Discuss your AI/Agentic AI initiative",
                "Assess technical fit, integrations, and delivery model",
                "Plan MVP, pilot, or production rollout",
              ].map((item, index) => (
                <div
                  key={index}
                  style={{
                    background: "#173f7c",
                    borderRadius: "20px",
                    padding: "18px 20px",
                    color: "#fff",
                    fontSize: px("15px", "14px", "14px"),
                    lineHeight: "1.45",
                    marginBottom: "16px",
                    fontWeight: "500",
                  }}
                >
                  {item}
                </div>
              ))}

              <div
                style={{
                  display: "flex",
                  flexDirection: isMobile ? "column" : "row",
                  gap: "14px",
                  marginTop: "22px",
                }}
              >
                <button
                  type="button"
                  onClick={() => (window.location.href = "mailto:contact@surensinfotek.com")}
                  style={{
                    background: "#ff7d1a",
                    border: "none",
                    color: "#fff",
                    padding: px("20px 20px", "16px 18px", "14px 16px"),
                    borderRadius: "18px",
                    fontSize: px("14px", "13px", "13px"),
                    fontWeight: "700",
                    cursor: "pointer",
                    width: isMobile ? "100%" : "auto",
                  }}
                >
                  contact@surensinfotek.com
                </button>

                <button
                  type="button"
                  onClick={() => navigate("/contact")}
                  style={{
                    background: "rgba(255,255,255,0.10)",
                    border: "1px solid rgba(255,255,255,0.15)",
                    color: "#fff",
                    padding: px("16px 22px", "14px 18px", "12px 16px"),
                    borderRadius: "18px",
                    fontSize: px("14px", "13px", "13px"),
                    fontWeight: "700",
                    cursor: "pointer",
                    width: isMobile ? "100%" : "auto",
                  }}
                >
                  Contact page
                </button>
              </div>
            </div>
          </div>
        </div>

        <div style={{ height: "1px", background: "#1cc7b7", width: "100%" }} />
      </div>
    </>
  );
}

export default AgenticAICapabilities;

